import { useEffect, useState } from "react";
import { KIND_COLORS } from "./SurveyPlanView";

const KIND_LABELS = {
  waypoint: "Waypoint",
  survey_grid: "Survey Grid",
  corridor_scan: "Corridor Scan",
  structure_scan: "Structure Scan",
  fence_inclusion: "Fence Inclusion (+)",
  fence_exclusion: "Fence Exclusion (−)",
};

function NumberField({ label, value, onChange, step = 1, min = 0, hint }) {
  return (
    <div className="cs-field">
      <label>{label}</label>
      <input
        type="number"
        value={value}
        step={step}
        min={min}
        onChange={(e) => onChange(parseFloat(e.target.value || 0))}
      />
      {hint && <span className="cs-hint">{hint}</span>}
    </div>
  );
}

function ItemForm({ item, onUpdate }) {
  const p = item.parameters || {};
  const setP = (k) => (v) => onUpdate(item.id, { parameters: { ...p, [k]: v } });

  switch (item.kind) {
    case "waypoint":
      return (
        <>
          <NumberField label="Altitude (m)" value={item.altitude} step={1} min={1}
            onChange={(v) => onUpdate(item.id, { altitude: v })} />
          <NumberField label="Hover duration (s)" value={item.hoverDuration ?? 0} step={1} min={0}
            onChange={(v) => onUpdate(item.id, { hoverDuration: v })} />
        </>
      );
    case "survey_grid":
      return (
        <>
          <NumberField label="Grid angle (deg from N)" value={p.gridAngleDeg ?? 90} step={5} min={0}
            onChange={setP("gridAngleDeg")} />
          <NumberField label="Line spacing (m)" value={p.spacing ?? 10} step={1} min={1}
            onChange={setP("spacing")} />
          <NumberField label="Altitude (m)" value={p.altitude ?? 30} step={1} min={1}
            onChange={setP("altitude")} />
          <NumberField label="Turnaround distance (m)" value={p.turnaroundDistance ?? 0} step={1} min={0}
            onChange={setP("turnaroundDistance")} />
          <label className="cs-check">
            <input type="checkbox" checked={!!p.crossGrid}
              onChange={(e) => setP("crossGrid")(e.target.checked)} />
            Cross grid (double pass)
          </label>
        </>
      );
    case "corridor_scan":
      return (
        <>
          <NumberField label="Corridor width (m)" value={p.width ?? 20} step={1} min={2}
            onChange={setP("width")} />
          <NumberField label="Line spacing (m)" value={p.spacing ?? 10} step={1} min={1}
            onChange={setP("spacing")} />
          <NumberField label="Altitude (m)" value={p.altitude ?? 30} step={1} min={1}
            onChange={setP("altitude")} />
        </>
      );
    case "structure_scan":
      return (
        <>
          <NumberField label="Altitude (m)" value={p.altitude ?? 30} step={1} min={1}
            onChange={setP("altitude")} />
          <NumberField label="Perimeter offset (m)" value={p.offset ?? 0} step={1} min={0}
            onChange={setP("offset")} />
          <NumberField label="Loops" value={p.levels ?? 1} step={1} min={1}
            onChange={setP("levels")} />
        </>
      );
    case "fence_inclusion":
    case "fence_exclusion":
      return (
        <>
          <div className="cs-field">
            <label>Fence type</label>
            <select
              value={item.kind}
              onChange={(e) => onUpdate(item.id, { kind: e.target.value })}
            >
              <option value="fence_inclusion">Inclusion</option>
              <option value="fence_exclusion">Exclusion</option>
            </select>
          </div>
          <span className="cs-hint">
            {item.polygon?.length ?? 0} boundary points — drag the shape or its
            vertices on the map to adjust.
          </span>
        </>
      );
    default:
      return null;
  }
}

/** Numeric input that lets you type freely but snaps to the committed value. */
function CoordField({ placeholder, ariaLabel, value, onCommit }) {
  const [text, setText] = useState(value ?? "");
  useEffect(() => {
    setText((t) => {
      const n = parseFloat(t);
      return Number.isFinite(n) && n === value ? t : value ?? "";
    });
  }, [value]);
  return (
    <input
      type="number"
      step="any"
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const n = parseFloat(e.target.value);
        if (Number.isFinite(n)) onCommit(n);
      }}
      onBlur={() => {
        const n = parseFloat(text);
        setText(Number.isFinite(n) && n === value ? text : value ?? "");
      }}
    />
  );
}

function ItemEditorPanel({
  items,
  selectedId,
  onSelect,
  onUpdate,
  onDelete,
  geofence,
  onGeofenceChange,
  setup,
  onSetupChange,
  onPickHome,
  pickingHome,
}) {
  const home = setup.homeLocation;
  return (
    <div className="cs-editor">
      <h3>Launch Setup</h3>
      <div className="cs-form">
        <div className="cs-field">
          <label>Home location</label>
          <div className="cs-row2">
            <CoordField
              placeholder="Latitude"
              ariaLabel="Home latitude"
              value={home?.lat ?? ""}
              onCommit={(v) =>
                onSetupChange((s) => ({
                  ...s,
                  homeLocation: { ...(s.homeLocation || {}), lat: v },
                }))
              }
            />
            <CoordField
              placeholder="Longitude"
              ariaLabel="Home longitude"
              value={home?.lng ?? ""}
              onCommit={(v) =>
                onSetupChange((s) => ({
                  ...s,
                  homeLocation: { ...(s.homeLocation || {}), lng: v },
                }))
              }
            />
          </div>
          <button
            type="button"
            className={`cs-pick-home-btn ${pickingHome ? "picking" : ""} ${home ? "" : "unset"}`}
            onClick={onPickHome}
            title="Click the map to set the home / launch point"
          >
            {pickingHome ? "Click the map…" : "Pick on map"}
          </button>
          <span className="cs-hint">Type coordinates or pick the point on the map.</span>
        </div>
        <div className="cs-field">
          <label>Takeoff elevation (m)</label>
          <input
            type="number"
            min={1}
            step={1}
            value={setup.takeoffAltitude}
            onChange={(e) =>
              onSetupChange((s) => ({ ...s, takeoffAltitude: parseFloat(e.target.value || 0) }))
            }
          />
          <span className="cs-hint">Climb altitude right after takeoff.</span>
        </div>
        <div className="cs-field">
          <label>Camera capture interval (s)</label>
          <input
            type="number"
            min={0}
            step={1}
            value={setup.cameraIntervalS ?? 0}
            onChange={(e) =>
              onSetupChange((s) => ({ ...s, cameraIntervalS: parseFloat(e.target.value || 0) }))
            }
          />
          <span className="cs-hint">0 = no interval capture. Requires the FCU CameraTrigger output wired to the camera.</span>
        </div>
        <label className="cs-check">
          <input
            type="checkbox"
            checked={setup.rtlAtEnd}
            onChange={(e) => onSetupChange((s) => ({ ...s, rtlAtEnd: e.target.checked }))}
          />
          Return to Launch (RTL) after mission
        </label>
      </div>

      {(!home || !(setup.takeoffAltitude > 0)) && (
        <div className="cs-setup-gate">
          ⚠ Set home location and takeoff elevation to start planning waypoints
          and grids.
        </div>
      )}

      <h3>Mission Items</h3>
      {items.length === 0 && setup.homeLocation && setup.takeoffAltitude > 0 && (
        <span className="cs-hint">
          Use the toolbar to add waypoints and mapping patterns on the map.
        </span>
      )}
      <div className="cs-item-list">
        {items.map((item, idx) => (
          <button
            key={item.id}
            className={`cs-item-chip ${selectedId === item.id ? "selected" : ""}`}
            onClick={() => onSelect(item.id)}
          >
            <span>
              {idx + 1}.{" "}
              <span className="cs-chip-kind" style={{ color: KIND_COLORS[item.kind] }}>
                {KIND_LABELS[item.kind] || item.kind}
              </span>
              {item.kind === "waypoint"
                ? ` · ${item.altitude} m`
                : item.parameters?.altitude != null
                ? ` · ${item.parameters.altitude} m`
                : ""}
            </span>
            <span className="cs-chip-del" onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}>
              ✕
            </span>
          </button>
        ))}
      </div>

      {selectedId &&
        (() => {
          const item = items.find((it) => it.id === selectedId);
          if (!item) return null;
          return (
            <>
              <h3>Selected: {KIND_LABELS[item.kind]}</h3>
              <div className="cs-form">
                <ItemForm item={item} onUpdate={onUpdate} />
              </div>
            </>
          );
        })()}

      <h3 style={{ marginTop: 8 }}>Geofence</h3>
      <div className="cs-form">
        <label className="cs-check">
          <input
            type="checkbox"
            checked={geofence.enabled}
            onChange={(e) => onGeofenceChange({ ...geofence, enabled: e.target.checked })}
          />
          Enable onboard geofence
        </label>
        <div className="cs-field">
          <label>Breach action</label>
          <select
            value={geofence.action}
            onChange={(e) => onGeofenceChange({ ...geofence, action: e.target.value })}
          >
            <option value="RTL">Return to Launch</option>
            <option value="Land">Land</option>
            <option value="None">Report only</option>
          </select>
        </div>
        <span className="cs-hint">
          {(() => {
            const keepIn = items.filter((i) => i.kind === "fence_inclusion").length;
            const keepOut = items.filter((i) => i.kind === "fence_exclusion").length;
            if (!keepIn && !keepOut) {
              return "Draw keep-in (+) or keep-out (−) rings with the fence tools — any shape, each ring is its own Mission Item.";
            }
            return `${keepIn} keep-in · ${keepOut} keep-out — select a fence in Mission Items to move, reshape or delete it. Rings work alone or combined.`;
          })()}
        </span>
      </div>
    </div>
  );
}

export default ItemEditorPanel;
